import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { ChatMessage } from '../types';
import type { ChatTarget } from '../types';
import { getThreadCache, setThreadCache, mergeMessages } from '../cache/messageCache';
import { MESSAGE_SELECT_FIELDS } from '../messageSelectFields';
import { chatKey, filterColumn, targetId } from '../chatTarget';

const PAGE_SIZE = 50;
const POLL_NEW_MESSAGES_MS = 5000;

export function useChatMessages(target: ChatTarget) {
  // Derived primitives, not the target object: callers build the target inline, so
  // depending on it directly would re-run every effect on every render.
  const key = chatKey(target);
  const col = filterColumn(target);
  const id = targetId(target);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  // Viewer id comes from the auth context, never from `supabase.auth.getUser()`:
  // getUser() hits /auth/v1/user, and a 403 there makes auth-js drop the stored
  // session and emit SIGNED_OUT, which bounces the user to /login just for
  // opening a chat. The context value is a local read and cannot do that.
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? null;

  const messagesRef = useRef<ChatMessage[]>([]);
  const currentUserIdRef = useRef<string | null>(null);
  // Mirrored during render so effects reading the ref never lag a tick behind
  // the context (mark-as-read bails out when the id is missing).
  currentUserIdRef.current = currentUserId;
  // Track hasMore in a ref so the realtime handler can write-through without needing it as a dep
  const hasMoreRef = useRef(true);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // HTTP fallback when Realtime misses inserts (e.g. publication / WS issues)
  useEffect(() => {
    if (loading || !id) return;

    let cancelled = false;

    const pollNewerMessagesFromServer = async () => {
      if (cancelled) return;

      const list = messagesRef.current;

      let query = supabase
        .from('messages_new')
        .select(MESSAGE_SELECT_FIELDS)
        .eq(col, id);

      if (list.length > 0) {
        const newestCreatedAt = list[list.length - 1].created_at;
        query = query.gt('created_at', newestCreatedAt).order('created_at', { ascending: true });
      } else {
        query = query.order('created_at', { ascending: false }).limit(PAGE_SIZE);
      }

      const { data, error } = await query;

      if (cancelled) return;
      if (error) {
        console.error('[EventChat] Poll newer messages failed', error);
        return;
      }

      let rows = (data as ChatMessage[] | null) ?? [];
      if (list.length === 0 && rows.length > 0) {
        rows = [...rows].reverse();
      }

      if (rows.length === 0) return;

      setMessages((prev) => {
        const next = mergeMessages(prev, rows);
        void setThreadCache(key, { messages: next, hasMore: hasMoreRef.current });
        return next;
      });
    };

    const refreshNewMessagesIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void pollNewerMessagesFromServer();
    };

    refreshNewMessagesIfVisible();
    document.addEventListener('visibilitychange', refreshNewMessagesIfVisible);
    const intervalId = window.setInterval(() => {
      refreshNewMessagesIfVisible();
    }, POLL_NEW_MESSAGES_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', refreshNewMessagesIfVisible);
      window.clearInterval(intervalId);
    };
  }, [col, id, key, loading]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        // 1. Try to hydrate from cache immediately
        const cached = await getThreadCache(key);

        if (!isMounted) return;

        if (cached) {
          // Show cached messages right away — no loading spinner
          setMessages(cached.messages);
          setHasMore(cached.hasMore);
          hasMoreRef.current = cached.hasMore;
          setLoading(false);
        } else {
          // First time opening this chat
          setMessages([]);
          setHasMore(true);
          hasMoreRef.current = true;
          setLoading(true);
        }

        // 2. Fetch messages — strategy depends on whether we have a cache
        if (cached && cached.messages.length > 0) {
          // Fetch ONLY messages newer than what we already have
          const newestCreatedAt = cached.messages[cached.messages.length - 1].created_at;

          const { data, error } = await supabase
            .from('messages_new')
            .select(MESSAGE_SELECT_FIELDS)
            .eq(col, id)
            .gt('created_at', newestCreatedAt)
            .order('created_at', { ascending: true });

          if (!isMounted) return;
          if (error) {
            console.error('[EventChat] Failed to fetch newer messages', error);
            return;
          }

          const newer = (data as ChatMessage[] | null) ?? [];
          if (newer.length > 0) {
            const merged = mergeMessages(cached.messages, newer);
            setMessages(merged);
            await setThreadCache(key, { messages: merged, hasMore: hasMoreRef.current });
          }
          // If no newer messages, cached state is already correct — nothing to do
        } else {
          // No cache — full initial fetch (latest PAGE_SIZE, desc → reverse to asc)
          const { data, error } = await supabase
            .from('messages_new')
            .select(MESSAGE_SELECT_FIELDS)
            .eq(col, id)
            .order('created_at', { ascending: false })
            .limit(PAGE_SIZE);

          if (!isMounted) return;

          if (error) {
            console.error('[EventChat] Failed to load messages', error);
            setMessages([]);
            setHasMore(false);
            hasMoreRef.current = false;
            return;
          }

          const initial = (data as ChatMessage[] | null) ?? [];
          const chronological = [...initial].reverse();
          const newHasMore = initial.length >= PAGE_SIZE;

          setMessages(chronological);
          setHasMore(newHasMore);
          hasMoreRef.current = newHasMore;

          // Write through to cache
          await setThreadCache(key, { messages: chronological, hasMore: newHasMore });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInitialData();

    const messagesChannel = supabase
      .channel(`messages_new:${key}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages_new',
          filter: `${col}=eq.${id}`,
        },
        (payload) => {
          if (!isMounted) return;
          const newRow = payload.new as ChatMessage;
          // Defensive re-check: the server filter should already guarantee this.
          if ((newRow as unknown as Record<string, unknown>)[col] !== id) return;

          setMessages((prev) => {
            const next = mergeMessages(prev, [newRow]);
            // Write-through to IndexedDB (fire-and-forget; best-effort)
            setThreadCache(key, { messages: next, hasMore: hasMoreRef.current });
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(messagesChannel);
    };
  }, [col, id, key]);

  // When hasMore transitions to false (e.g. "See older messages" exhausted), persist it
  const prevHasMoreRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading) return;
    if (prevHasMoreRef.current === false && hasMore === false) return; // already false, skip
    if (hasMore === false && prevHasMoreRef.current !== false) {
      // hasMore just became false — update the cache entry
      setMessages((prev) => {
        setThreadCache(key, { messages: prev, hasMore: false });
        return prev;
      });
    }
    prevHasMoreRef.current = hasMore;
  }, [hasMore, loading, key]);

  return {
    messages,
    setMessages,
    loading,
    hasMore,
    setHasMore,
    currentUserId,
    currentUserIdRef,
  };
}
